/**
 * Password generator + recovery key formatting.
 *
 * Generation uses rejection sampling over the CSPRNG to eliminate modulo bias.
 */
import { randomBytes, toBytes, type Bytes } from './encoding.js';

export interface GeneratorOptions {
  length: number; // 8..128
  uppercase?: boolean;
  lowercase?: boolean;
  numbers?: boolean;
  symbols?: boolean;
  avoidAmbiguous?: boolean; // exclude I l 1 O 0 etc.
}

const SETS = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  symbols: '!@#$%^&*()-_=+[]{};:,.?/',
};

const AMBIGUOUS = new Set(['I', 'l', '1', 'O', '0', 'o', '|', '`', "'", '"']);

function buildPool(opts: GeneratorOptions): string {
  let pool = '';
  if (opts.uppercase) pool += SETS.uppercase;
  if (opts.lowercase) pool += SETS.lowercase;
  if (opts.numbers) pool += SETS.numbers;
  if (opts.symbols) pool += SETS.symbols;
  if (opts.avoidAmbiguous) pool = [...pool].filter((c) => !AMBIGUOUS.has(c)).join('');
  return pool;
}

/**
 * Pick a uniformly-random index into `pool` using rejection sampling so the
 * distribution is unbiased even when pool.length does not divide 256.
 */
function unbiasedIndex(poolLen: number): number {
  if (poolLen <= 0) throw new Error('Empty character pool');
  const limit = Math.floor(256 / poolLen) * poolLen; // largest multiple of poolLen <= 256
  // Rejection sampling: draw bytes until one is < limit.
  for (let attempts = 0; attempts < 64; attempts++) {
    const b = randomBytes(1)[0]!;
    if (b < limit) return b % poolLen;
  }
  // Extremely unlikely fallback (still unbiased-ish over a single draw within limit).
  return randomBytes(1)[0]! % poolLen;
}

/** Generate a strong password from the given options. */
export function generatePassword(opts: GeneratorOptions): string {
  const length = Math.max(8, Math.min(128, Math.floor(opts.length ?? 20)));
  const pool = buildPool(opts);
  if (!pool) throw new Error('At least one character class must be enabled');

  const chars: string[] = [];
  // Guarantee at least one of each enabled class (then fill remaining).
  const classes: string[] = [];
  if (opts.uppercase) classes.push(SETS.uppercase);
  if (opts.lowercase) classes.push(SETS.lowercase);
  if (opts.numbers) classes.push(SETS.numbers);
  if (opts.symbols) classes.push(SETS.symbols);

  for (const cls of classes) {
    const filtered = opts.avoidAmbiguous ? [...cls].filter((c) => !AMBIGUOUS.has(c)).join('') : cls;
    if (filtered) chars.push(filtered[unbiasedIndex(filtered.length)]!);
  }
  while (chars.length < length) chars.push(pool[unbiasedIndex(pool.length)]!);

  // Fisher-Yates shuffle with CSPRNG indices so guaranteed chars are dispersed.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = unbiasedIndex(i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.slice(0, length).join('');
}

/**
 * Generate a recovery key: 256 bits of entropy formatted as 20 groups of
 * base32-ish characters with a checksum. Shown to the user ONCE.
 */
const RECOVERY_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no ambiguous

export function generateRecoveryKey(): string {
  // 256 bits of entropy encoded as 32 chars from RECOVERY_ALPHABET (base32-ish),
  // plus a 2-char checksum — rejection-sampled per char to avoid bias.
  const chars: string[] = [];
  for (let i = 0; i < 32; i++) chars.push(RECOVERY_ALPHABET[unbiasedIndex(RECOVERY_ALPHABET.length)]!);
  // Append a 2-char checksum over the raw key so typos can be caught client-side.
  const check = checksumChars(chars.join(''));
  const all = chars.join('') + check;
  // Group every 4 chars, joined by '-'.
  return all.match(/.{1,4}/g)!.join('-');
}

export function normalizeRecoveryKey(input: string): string {
  return input.replace(/[^A-Z2-9]/gi, '').toUpperCase();
}

/** Validate the checksum portion of a recovery key. */
export function validateRecoveryKey(input: string): boolean {
  const norm = normalizeRecoveryKey(input);
  if (norm.length !== 34) return false;
  const body = norm.slice(0, 32);
  const check = norm.slice(32);
  return checksumChars(body) === check;
}

function checksumChars(s: string): string {
  const bytes = toBytes(s);
  const h = new Uint8Array(bytes.length);
  let sum = 0;
  for (let i = 0; i < bytes.length; i++) {
    sum = (sum * 31 + bytes[i]!) % 9007199254740991;
    h[i] = bytes[i]!;
  }
  // Derive 2 chars from a hash of the body.
  const digest = sum & 0x3ff; // 10 bits
  return (
    RECOVERY_ALPHABET[digest % RECOVERY_ALPHABET.length]! +
    RECOVERY_ALPHABET[Math.floor(digest / RECOVERY_ALPHABET.length) % RECOVERY_ALPHABET.length]!
  );
}

/** Derive a 32-byte key from a recovery key string (normalized). */
export async function deriveRecoveryKey(normRecoveryKey: string): Promise<Bytes> {
  const keyMaterial = await crypto.subtle.importKey('raw', toBytes(normRecoveryKey), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: toBytes('battini-recovery-v1'), iterations: 100_000, hash: 'SHA-256' },
    keyMaterial,
    256,
  );
  return new Uint8Array(bits);
}

/** Rough entropy/strength estimate for the security dashboard (0..4). */
export function estimateStrength(password: string): { score: 0 | 1 | 2 | 3 | 4; label: string; guesses: number } {
  let pool = 0;
  if (/[a-z]/.test(password)) pool += 26;
  if (/[A-Z]/.test(password)) pool += 26;
  if (/[0-9]/.test(password)) pool += 10;
  if (/[^a-zA-Z0-9]/.test(password)) pool += 32;
  const guesses = pool > 0 ? Math.pow(pool, password.length) : 0;
  const bits = guesses > 0 ? Math.log2(guesses) : 0;
  const score = bits < 28 ? 0 : bits < 36 ? 1 : bits < 60 ? 2 : bits < 128 ? 3 : 4;
  const label = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'][score]!;
  return { score, label, guesses };
}
